import React from "react";
import "../../../css/Common/Components/DesignSystem/Footer.css";
import { ReactComponent as FacebookIcon } from "../../../assets/icons/facebook.svg";
import { ReactComponent as LinkedInIcon } from "../../../assets/icons/LinkedIn.svg";
import { ReactComponent as TwitterIcon } from "../../../assets/icons/x.svg";
import { ReactComponent as YouTubeIcon } from "../../../assets/icons/youtube.svg";
import { Link } from "react-router-dom";
import { useState } from "react";
import { RequestCallback } from "../ContactForm/RequestCallback";
import { useNavigate, useLocation } from "react-router-dom";

/**
 * Footer component displays the website's footer section, including company information,
 * navigation links, contact details, social media links, and legal disclaimers.
 * 
 * @component
 * @param {Object} props - Component props.
 * @param {boolean} [props.isAdviser=false] - Determines if the user is an adviser, which affects the visibility of certain footer items.
 * @returns {JSX.Element} The rendered Footer component.
 *
 * @example
 * <Footer isAdviser={false} />
 *
 * Features:
 * - Displays company, resources, contact, and address columns.
 * - Shows different options for advisers and non-advisers.
 * - Provides clickable phone, email, and address links.
 * - Includes social media icons with external links.
 * - Shows a callback request modal when triggered.
 */
export const Footer = ({ isAdviser = false }) => {
  const [isCallbackVisible, setCallbackVisible] = useState(false);
  const [title, setTitle] = useState("");
  const currentYear = new Date().getFullYear();

  /**
   * Opens the callback modal and optionally sets its title.
   *
   * @function
   * @param {string} [title=""] - The title to set for the callback modal. If not provided, the title remains unchanged.
   * @returns {void}
   */
  const handleOpenCallback = (title = "") => {
    setCallbackVisible(true);
    title && setTitle(title);
    // console.log("callback");
  };

  /**
   * Handles the close action for the callback component.
   * Sets the callback visibility state to false, effectively hiding the callback UI.
   *
   * @function
   * @returns {void}
   */
  const handleCloseCallback = () => {
    setCallbackVisible(false);
  };
  const location = useLocation();
  return (
    <div className="Footer">
      <div className="Footer-Content">
        <div className="Footer-Content-Column">
          <h3>Company</h3>
          <div className="Footer-Content-Column">
            <Link to="https://www.adviser.rosemountfs-ifa.co.uk/">
              <div className="Footer-Item">Log In</div>
            </Link>

            <Link to="/Adviser">
              <div className="Footer-Item">Advisers</div>
            </Link>

            <Link to="/Customer">
              <div className="Footer-Item">Customers</div>
            </Link>
            <Link to="/AboutUs">
              <div className="Footer-Item">About Us</div>
            </Link>
            <Link to="https://www.staff.rosemountfs-ifa.co.uk/">
              <div className="Footer-Item">Staff Portal</div>
            </Link>
            <Link to="/PrivacyPolicy">
              <div className="Footer-Item">Privacy Notice</div>
            </Link>
          </div>
        </div>
        <div className="Footer-Content-Column">
          <h3>Resources</h3>
          <div className="Footer-Content-Column">
            {isAdviser === false && (
              <div
                className="Footer-Item"
                onClick={() => handleOpenCallback("Free Initial Consultation")}
              >
                Financial Advice
              </div>
            )}
            <Link to="/RosemountInMedia">
              <div className="Footer-Item">News</div>
            </Link>
            <Link to="/RosemountBlog">
              <div className="Footer-Item">Blog</div>
            </Link>
          </div>
        </div>
        <div className="Footer-Content-Column">
          <h3>Contact Us</h3>
          <div className="Footer-Content-Column">
            <div
              className="Footer-Item"
              onClick={() => (window.location.href = `tel: 01256 405460`)}
            >
              01256 405460
            </div>
            {isAdviser === false ? (
              <div
                className="Footer-Item"
                onClick={() => handleOpenCallback("Request a Callback")}
              >
                Request a Callback
              </div>
            ) : null}
            <div
              className="Footer-Item"
              onClick={() =>
                (window.location.href = `mailto:Info@rosemountfsifa.co.uk`)
              }
            >
              Info@rosemountfsifa.co.uk
            </div>
          </div>
        </div>
        <div className="Footer-Content-Column">
          <h3>Address:</h3>
          <div className="Footer-Content-Column">
            <div
              className="Footer-Item"
              onClick={() =>
                window.open(
                  "https://www.google.com/maps/place/Chequers+Rd,+Basingstoke+RG21+7PU/@51.2642792,-1.0857268,17z/data=!3m1!4b1!4m6!3m5!1s0x487421823c4ad3d5:0xaa143b27ef432655!8m2!3d51.2643673!4d-1.0832537!16s%2Fg%2F1tfkvs6w?entry=ttu&g_ep=EgoyMDI0MTIwOC4wIKXMDSoASAFQAw%3D%3D",
                  "_blank",
                )
              }
            >
              Rosemount House, 2 – 4 Chequers Road, Basingstoke, RG21 7PU
            </div>
          </div>
        </div>
      </div>
      <div className="Footer-SocialMedia-Options">
        <div
          className="Footer-SocialMedia-Icon"
          onClick={() =>
            window.open(
              "https://www.linkedin.com/company/rosemount-financial-solutions-ifa-ltd/",
              "_blank",
            )
          }
        >
          <LinkedInIcon className="SocialMedia-Icon" />
          <div className="Label-Small-SemiBold">LinkedIn</div>
        </div>
        <div
          className="Footer-SocialMedia-Icon"
          onClick={() =>
            window.open("https://www.facebook.com/rosemountifa/", "_blank")
          }
        >
          <FacebookIcon className="SocialMedia-Icon" />
          <div className="Label-Small-SemiBold">Facebook</div>
        </div>
        {/* <div
          className="Footer-SocialMedia-Icon"
          onClick={() =>
            window.open(
              "https://x.com/i/flow/login?redirect_after_login=%2FRosemountIfa",
              "_blank",
            )
          }
        >
          <TwitterIcon className="SocialMedia-Icon" />
          <div className="Label-Small-SemiBold">X(Twitter)</div>
        </div> */}
        <div
          className="Footer-SocialMedia-Icon"
          onClick={() =>
            window.open(
              "https://www.youtube.com/channel/UCk0mdvSWPlYj3gcrmbai6QA",
              "_blank",
            )
          }
        >
          <YouTubeIcon className="SocialMedia-Icon" />
          <div className="Label-Small-SemiBold">YouTube</div>
        </div>
      </div>
      <div className="Footer-Container">
        <div className="Content-Small">
          Rosemount Financial Solutions (IFA) Ltd is authorised and regulated by
          the Financial Conduct Authority (FCA). Entered on the FCA Register{" "}
          <a
            href="https://register.fca.org.uk/"
            target="_blank"
            rel="noopener noreferrer"
          >
            (https://register.fca.org.uk/){" "}
          </a>
          under reference 535515. Rosemount Financial Solutions (IFA) Ltd is
          registered in England and Wales, Registered No. 05668905, Registered
          Address Rosemount House, 2 – 4 Chequers Road, Basingstoke, RG21 7PU. 
        </div>
      </div>
      <div className="Content-Small">
        The information contained within this website is subject to the UK
        regulatory regime, and is therefore targeted at consumers based in the
        UK.
      </div>
      <div className="Content-Small">
        Copyright © {currentYear} Rosemount Financial Solutions IFA Ltd | Powered by
        Rosemount Financial Solutions IFA Ltd
      </div>
      {isCallbackVisible && (
        <div className="notification-overlay">
          <RequestCallback
            title={title || "Request a Callback"}
            onClose={() => handleCloseCallback()}
          />
        </div>
      )}
    </div>
  );
};

export default Footer;
